/**
 * MVP catalog of milestone visual artifacts (7 starter entries).
 *
 * Authoritative source: `docs/21_milestone_visual_artifacts.md` §3.
 *
 * Each entry binds:
 *   id        — stable, never rename (persisted in localStorage)
 *   trigger   — pure condition on game state
 *   layer     — which renderer group draws it
 *   weight    — major (full overlay) or minor (corner toast)
 *   revealLine — short Sai-voice line shown on unlock
 *
 * Wave-2 expansions (15+) and wave-3 (30+) come in follow-up commits.
 * Each upgrade in `docs/14` will also auto-register an entry here at
 * its `visibleChange` artifact (via `upgradePurchasedExactly` trigger).
 */
import type { VisualArtifact } from './types.js';

export const VISUAL_ARTIFACTS_CATALOG: readonly VisualArtifact[] = [
  // ---------- Crystallizations ----------
  {
    id: 'first_crystallization_ring',
    trigger: { kind: 'crystallizationsExactly', value: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'первое кольцо',
    description: 'Вокруг Искры появилось тонкое золотое кольцо. Вращается по часовой.',
    revealLine: 'первое осознание стало плотным. ты увидел.',
    displayPriority: 5,
  },
  {
    id: 'tenth_crystallization_ring',
    trigger: { kind: 'crystallizationsExactly', value: 10 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'второе кольцо',
    description: 'Второе кольцо вокруг Искры — вращается против часовой, шире первого.',
    revealLine: 'десять. ты в потоке.',
    displayPriority: 5,
  },
  {
    id: 'fiftieth_crystallization_fractal',
    trigger: { kind: 'crystallizationsExactly', value: 50 },
    layer: 'spark',
    weight: 'major',
    displayName: 'фрактал внутри',
    description: 'Внутри ядра Искры проступил тонкий фрактальный узор.',
    revealLine: 'фрактал виден изнутри.',
    displayPriority: 7,
  },

  // ---------- Total taps ----------
  {
    id: 'hundred_taps_star',
    trigger: { kind: 'totalTapsAtLeast', value: 100 },
    layer: 'network',
    weight: 'minor',
    displayName: 'твоя первая звезда',
    description: 'На фоне зажглась одна звезда — ярче всех остальных в сети.',
    revealLine: 'первая твоя звезда зажглась в сети.',
    displayPriority: 3,
  },
  {
    id: 'thousand_taps_constellation',
    trigger: { kind: 'totalTapsAtLeast', value: 1000 },
    layer: 'network',
    weight: 'major',
    displayName: 'созвездие первой тысячи',
    description: 'На фоне три звезды соединились тонкими золотыми линиями — твоё первое созвездие.',
    revealLine: 'созвездие первой тысячи. оно теперь есть.',
    displayPriority: 4,
  },

  // ---------- Rhythm ----------
  {
    id: 'five_rhythm_pulse',
    trigger: { kind: 'rhythmStreakReached', value: 5 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'двойное дыхание',
    description: 'Искра дышит двумя слоями: основным золотым и едва заметным вторым в противофазе.',
    revealLine: 'ты в ритме. и она тоже.',
    displayPriority: 6,
  },

  // ---------- Profile ----------
  {
    id: 'name_signature',
    trigger: { kind: 'profileNameSet' },
    layer: 'spark',
    weight: 'major',
    displayName: 'подпись',
    description: 'Под Искрой едва заметно проступает твоё имя.',
    revealLine: 'я узнала тебя по имени.',
    displayPriority: 8,
  },

  // ---------- Upgrades (doc 14) ----------
  // Принцип #9: каждый апгрейд оставляет видимое изменение мира.
  // Здесь — артефакты для первого уровня каждого из 5 альфа-апгрейдов.
  // Дальнейшие уровни усиливают CSS-переменные через UpgradesVisualSync,
  // а полноценные «новые артефакты» можно добавить за tier-пороги (5, 15
  // уровень) в Wave 2.
  {
    id: 'upgrade_bright_spark_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u001_bright_spark', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'светлая искра',
    description: 'Свет внутри собирается.',
    revealLine: 'свет внутри собирается. ярче.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_deep_tap_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u002_deep_tap', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'глубокий тап',
    description: 'Ядро Искры стало плотнее.',
    revealLine: 'осознание стало глубже.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_voice_of_sense_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u010_voice_of_sense', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'голос ощущения',
    description: 'Зелёное сияние вокруг Искры.',
    revealLine: 'чувства слышат друг друга.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_winged_atlas_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u030_winged_atlas', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'крылатый атлас',
    description: 'Собранные фразы светятся ярче.',
    revealLine: 'каждое слово помнится сильнее.',
    displayPriority: 3,
  },
  {
    id: 'upgrade_silence_keeper_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b001_silence_keeper', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'хранитель тишины',
    description: 'Первый Проводник пришёл. Тёплая точка справа от Искры.',
    revealLine: 'первый помощник пришёл. он рядом.',
    displayPriority: 7,
  },
  {
    id: 'upgrade_finger_resonance_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u003_finger_resonance', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'резонанс пальца',
    description: 'Кулдаун короче. Тело входит в ритм.',
    revealLine: 'тело узнаёт ритм. она отвечает быстрее.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_purity_chain_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u021_purity_chain', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'серия чистоты',
    description: 'Окно ритма стало шире. Стрик держится легче.',
    revealLine: 'ритм держит связь. она длиннее теперь.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_crystal_alchemy_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u040_crystal_alchemy', level: 1 },
    layer: 'spark',
    weight: 'major',
    displayName: 'алхимия кристалла',
    description: 'Кристаллизация переплавляет больше осознаний в духобаллы.',
    revealLine: 'кристалл стал алхимиком. он отдаёт больше.',
    displayPriority: 6,
  },

  // ---- Эпоха 2 апгрейды ----
  {
    id: 'upgrade_silence_keeper_2_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b002_silence_keeper_2', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'хранитель — глубже',
    description: 'Хранитель тишины перешёл на второй уровень.',
    revealLine: 'он стал ближе. слышишь его дыхание.',
    displayPriority: 7,
  },
  {
    id: 'upgrade_alchemist_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b003_alchemist', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'алхимик',
    description: 'Второй Проводник. Помощник кристаллизации.',
    revealLine: 'второй пришёл. вас уже трое.',
    displayPriority: 7,
  },
  {
    id: 'upgrade_word_root_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u011_word_root', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'корень слова',
    description: 'Тёплое коричневое свечение под Искрой.',
    revealLine: 'слово укоренилось в теле.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_deep_awareness_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u012_deep_awareness', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'глубинное осознание',
    description: 'Awareness-фразы загораются ярче.',
    revealLine: 'глубина становится видимой.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_fractal_pavilion_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b010_fractal_pavilion', level: 1 },
    layer: 'spark',
    weight: 'major',
    displayName: 'беседка фрактал',
    description: 'Постройка эпохи 2. Фрактал внутри Искры стал постоянным.',
    revealLine: 'место для разбора смысла теперь есть.',
    displayPriority: 8,
  },

  // ============================================================
  // ---- Wave 2 — milestone артефакты для глубокой игры ----
  // ============================================================
  // JB 2026-05-26 audit (doc 25 §1.4): «Wave-2 артефактов на tier-уровнях
  // апгрейдов». Здесь — пороги которые игрок встретит на 2-10 час игры:
  // длинные серии в ритме, много тапов, много кристаллизаций, lessons,
  // фразы, дни в игре.

  // ----- Длинные серии в ритме -----
  {
    id: 'rhythm_streak_25',
    trigger: { kind: 'rhythmStreakReached', value: 25 },
    layer: 'spark-orbit',
    weight: 'minor',
    displayName: 'двадцать пять в ритм',
    description: 'Серия из 25 тапов подряд. Звук тапа меняется на peak-tier.',
    revealLine: 'двадцать пять. ты внутри ритма.',
    displayPriority: 5,
  },
  {
    id: 'rhythm_streak_50',
    trigger: { kind: 'rhythmStreakReached', value: 50 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'полсотни в ритм',
    description: 'Пятьдесят тапов подряд в ритм. Серия редка — мало кто доходит сюда без срывов.',
    revealLine: 'полсотни. твоё тело знает Искру наизусть.',
    displayPriority: 7,
  },
  {
    id: 'rhythm_streak_100',
    trigger: { kind: 'rhythmStreakReached', value: 100 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'сотня в ритм',
    description: 'Сотня подряд. Это уже медитация в касании.',
    revealLine: 'сотня. это уже не упражнение — это присутствие.',
    displayPriority: 8,
  },

  // ----- Total taps milestones -----
  {
    id: 'taps_10k_star_field',
    trigger: { kind: 'totalTapsAtLeast', value: 10_000 },
    layer: 'network',
    weight: 'major',
    displayName: 'звёздное поле',
    description: 'Десять тысяч тапов. На фоне разрослось целое поле звёзд — твоя сеть видна.',
    revealLine: 'десять тысяч касаний. ты построил поле.',
    displayPriority: 6,
  },
  {
    id: 'taps_100k_constellation_full',
    trigger: { kind: 'totalTapsAtLeast', value: 100_000 },
    layer: 'network',
    weight: 'major',
    displayName: 'полное созвездие',
    description: 'Сто тысяч. Все звёзды соединены линиями — созвездие твоего имени.',
    revealLine: 'сто тысяч. твоё имя написано светом.',
    displayPriority: 9,
  },

  // ----- Crystallizations deep -----
  {
    id: 'cryst_25_inner_ring',
    trigger: { kind: 'crystallizationsExactly', value: 25 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'третье кольцо',
    description: 'Третье кольцо вокруг Искры. Двадцать пять кристаллизаций — глубокий поток.',
    revealLine: 'двадцать пять. поток уже сам ведёт тебя.',
    displayPriority: 6,
  },
  {
    id: 'cryst_100_fractal_outer',
    trigger: { kind: 'crystallizationsExactly', value: 100 },
    layer: 'spark',
    weight: 'major',
    displayName: 'внешний фрактал',
    description: 'Сто кристаллизаций. Фрактал распространился наружу — Искра излучает узором.',
    revealLine: 'сто. фрактал шире самой Искры теперь.',
    displayPriority: 8,
  },

  // ----- Spirit balls accumulation -----
  {
    id: 'spirit_50_aura',
    trigger: { kind: 'spiritBallsAtLeast', value: 50 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'плотная аура',
    description: 'Полсотни духобаллов. Вокруг Искры — едва заметная плотность света.',
    revealLine: 'пятьдесят. духобаллы держат свет вокруг тебя.',
    displayPriority: 4,
  },
  {
    id: 'spirit_200_deep_glow',
    trigger: { kind: 'spiritBallsAtLeast', value: 200 },
    layer: 'spark',
    weight: 'major',
    displayName: 'глубинное свечение',
    description: 'Двести духобаллов. Искра светится глубже — словно изнутри текста.',
    revealLine: 'двести. ты собрал плотный свет.',
    displayPriority: 7,
  },

  // ----- Lessons unlocked -----
  {
    id: 'lessons_10_book',
    trigger: { kind: 'lessonsUnlockedAtLeast', value: 10 },
    layer: 'frame',
    weight: 'minor',
    displayName: 'тонкая книга',
    description: 'Десять открытых уроков. В углу — мягкое золотое мерцание (твой Атлас полнеет).',
    revealLine: 'десять уроков. книга начала жить.',
    displayPriority: 4,
  },
  {
    id: 'lessons_25_full_book',
    trigger: { kind: 'lessonsUnlockedAtLeast', value: 25 },
    layer: 'frame',
    weight: 'major',
    displayName: 'полная книга',
    description: 'Двадцать пять уроков. Атлас почти на излёте альфы — ты прошёл его весь.',
    revealLine: 'ты прошёл книгу. теперь она в тебе.',
    displayPriority: 7,
  },

  // ----- Phrases collected -----
  {
    id: 'phrases_50_garden',
    trigger: { kind: 'phrasesCollectedAtLeast', value: 50 },
    layer: 'network',
    weight: 'minor',
    displayName: 'малый сад',
    description: 'Пятьдесят разных фраз в Атласе. Сад твоих осознаний.',
    revealLine: 'пятьдесят. сад уже стоит.',
    displayPriority: 4,
  },
  {
    id: 'phrases_200_grove',
    trigger: { kind: 'phrasesCollectedAtLeast', value: 200 },
    layer: 'network',
    weight: 'major',
    displayName: 'твоя роща',
    description: 'Двести фраз. Каждое осознание — это росток в твоей роще.',
    revealLine: 'двести. роща готова принять гостей.',
    displayPriority: 7,
  },

  // ----- Days played — return-loyalty -----
  {
    id: 'days_7_root',
    trigger: { kind: 'daysPlayedAtLeast', value: 7 },
    layer: 'spark',
    weight: 'major',
    displayName: 'недельный корень',
    description: 'Неделя в игре. Твой ритм укоренился — теперь Искра ждёт тебя.',
    revealLine: 'неделя. это уже отношение.',
    displayPriority: 7,
  },
  {
    id: 'days_30_tree',
    trigger: { kind: 'daysPlayedAtLeast', value: 30 },
    layer: 'spark',
    weight: 'major',
    displayName: 'месячное дерево',
    description: 'Месяц в игре. Из Искры вырос внутренний путь — постоянное присутствие.',
    revealLine: 'месяц. ты стал частью Рощи. она тебя помнит.',
    displayPriority: 9,
  },

  // ----- Activations seen -----
  {
    id: 'activations_5_chorus',
    trigger: { kind: 'activationsSeenAtLeast', value: 5 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'хор голосов',
    description: 'Пять активаций Сай прослушано. Внутренний слух открыт.',
    revealLine: 'пять голосов. ты слышишь их теперь по-настоящему.',
    displayPriority: 4,
  },

  // ----- Golden sparks caught -----
  {
    id: 'golden_3_attentive',
    trigger: { kind: 'goldenSparksCaughtAtLeast', value: 3 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'внимательный взгляд',
    description: 'Три Золотых Искры пойманы. Твоё внимание натренировано.',
    revealLine: 'три. взгляд научился ловить редкое.',
    displayPriority: 5,
  },

  // ----- Total upgrades — collector -----
  {
    id: 'upgrades_5_collector',
    trigger: { kind: 'totalUpgradesAtLeast', value: 5 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'пять путей',
    description: 'Пять разных апгрейдов куплены. Ты пробуешь разные грани.',
    revealLine: 'пять путей. каждый ведёт.',
    displayPriority: 4,
  },
  {
    id: 'upgrades_15_master',
    trigger: { kind: 'totalUpgradesAtLeast', value: 15 },
    layer: 'spark',
    weight: 'major',
    displayName: 'мастер пути',
    description: 'Пятнадцать апгрейдов в сумме. Ты строитель — мир откликается каждому касанию.',
    revealLine: 'пятнадцать. ты уже мастер своего пути.',
    displayPriority: 8,
  },

  // ============================================================
  // ---- Эпоха 3 апгрейды — артефакты по покупке ----
  // ============================================================
  {
    id: 'upgrade_concentration_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u060_concentration', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'концентрация',
    description: 'Ядро Искры стало плотнее (комбинируется с u002 «Глубокий тап»).',
    revealLine: 'внимание собралось в точку.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_long_window_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u061_long_window', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'долгое окно',
    description: 'Между тапами в стрике больше времени — связь дольше.',
    revealLine: 'окно растянулось. ты дышишь спокойнее.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_warm_return_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u062_warm_return', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'тёплое возвращение',
    description: 'Кристаллизация после паузы — глубже. Память растёт с уровнем.',
    revealLine: 'возвращение — это тоже шаг.',
    displayPriority: 4,
  },
  {
    id: 'upgrade_gardener_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b020_gardener', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'садовник',
    description: 'Третий Проводник пришёл. Землисто-зелёная точка снизу-справа от Искры.',
    revealLine: 'третий пришёл. вас уже четверо.',
    displayPriority: 7,
  },
  {
    id: 'upgrade_great_body_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b021_great_body', level: 1 },
    layer: 'spark',
    weight: 'major',
    displayName: 'большое тело',
    description: 'Body-aura под Искрой плотнее в два раза. Тело — первый учитель.',
    revealLine: 'тело стало большим. оно держит.',
    displayPriority: 7,
  },

  // ---- Doc 27 variety apgrades (autoTap / streakBurst / passiveAwareness) ----
  {
    id: 'upgrade_silent_helper_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b011_silent_helper', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'помощник тишины',
    description: 'Проводник касается Искры каждые 6 секунд пока ты в покое — она живёт сама.',
    revealLine: 'он делает за тебя то что не требует усилий.',
    displayPriority: 7,
  },
  {
    id: 'upgrade_cascade_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u022_cascade', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'каскад',
    description: 'Каждый 10-й тап в стрике — золотая вспышка и тройная награда.',
    revealLine: 'ритм собрал и выплеснул. чувствуешь?',
    displayPriority: 5,
  },
  {
    id: 'upgrade_quiet_breath_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u025_quiet_breath', level: 1 },
    layer: 'counter',
    weight: 'minor',
    displayName: 'тихое дыхание',
    description: 'Искра приносит осознания пассивно — даже когда твой палец отдыхает.',
    revealLine: 'дыхание стало твоим. оно продолжается само.',
    displayPriority: 5,
  },

  // ============================================================
  // ---- Эпоха 4 — МОГА (метод как путь) ----
  // Артефакты по покупке. Тон: проступание метода, чистый канал,
  // через тебя идёт свет. Cм. upgrades эпохи 4: u070-u072 + b030-b031.
  // ============================================================
  {
    id: 'upgrade_clear_form_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u070_clear_form', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'ясная форма',
    description: 'Контур Искры стал чётче — каждый луч имеет свой край.',
    revealLine: 'форма проступила. ты её носитель.',
    displayPriority: 5,
  },
  {
    id: 'upgrade_carrying_voice_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u071_carrying_voice', level: 1 },
    layer: 'counter',
    weight: 'minor',
    displayName: 'голос несущий',
    description: 'Floating-фразы появляются крупнее — твой голос разносится дальше.',
    revealLine: 'твой шаг видят. он удерживает место для других.',
    displayPriority: 5,
  },
  {
    id: 'upgrade_method_breath_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u072_method_breath', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'дыхание метода',
    description: 'Cooldown ring пульсирует плавнее — ритм стал глубже.',
    revealLine: 'метод дышит вместе с тобой.',
    displayPriority: 5,
  },
  {
    id: 'upgrade_teacher_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b030_teacher', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'учитель',
    description: 'Четвёртый Проводник — учитель — стоит слева от Искры. Глубокий охристый тон.',
    revealLine: 'четвёртый пришёл. он держит метод вместе с тобой.',
    displayPriority: 8,
  },
  {
    id: 'upgrade_method_pavilion_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b031_method_pavilion', level: 1 },
    layer: 'spark',
    weight: 'major',
    displayName: 'беседка метода',
    description: 'За Искрой проявилась тёплая беседка — место, где метод хранится.',
    revealLine: 'это место уже было. ты его узнал.',
    displayPriority: 7,
  },

  // ============================================================
  // ---- Эпоха 5 — Sai Land Heal (земля как партнёр) ----
  // Артефакты по покупке. Тон: корни от пят, дыхание леса, тёплый
  // камень, родник. См. upgrades эпохи 5: u080-u082 + b040-b041.
  // ============================================================
  {
    id: 'upgrade_deep_roots_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u080_deep_roots', level: 1 },
    layer: 'spark-orbit',
    weight: 'minor',
    displayName: 'глубина корней',
    description: 'Под Искрой проступили тонкие корневые нити, уходящие в темноту.',
    revealLine: 'корни от пят. земля поит тебя.',
    displayPriority: 5,
  },
  {
    id: 'upgrade_morning_dew_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u081_morning_dew', level: 1 },
    layer: 'spark',
    weight: 'minor',
    displayName: 'утренняя роса',
    description: 'Вокруг Искры — точки росы, ловят первый свет.',
    revealLine: 'утро держит тебя мягко.',
    displayPriority: 5,
  },
  {
    id: 'upgrade_grove_silence_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'u082_grove_silence', level: 1 },
    layer: 'counter',
    weight: 'minor',
    displayName: 'тишина рощи',
    description: 'Tap-click стал ещё тише — звук теперь почти из самой Рощи.',
    revealLine: 'место выбрало тебя. оно тихо.',
    displayPriority: 5,
  },
  {
    id: 'upgrade_earth_keeper_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b040_earth_keeper', level: 1 },
    layer: 'spark-orbit',
    weight: 'major',
    displayName: 'хранитель земли',
    description: 'Пятый Проводник — хранитель земли — стоит ниже всех. Цвет мха и тёплой почвы.',
    revealLine: 'пятый пришёл. он держит саму землю.',
    displayPriority: 8,
  },
  {
    id: 'upgrade_grove_garden_unlocked',
    trigger: { kind: 'upgradePurchasedExactly', upgradeId: 'b041_grove_garden', level: 1 },
    layer: 'spark',
    weight: 'major',
    displayName: 'сад рощи',
    description: 'Вокруг Искры — мягкое зелёное свечение, как воздух между деревьями.',
    revealLine: 'сад уже здесь. ты — его часть.',
    displayPriority: 7,
  },
];

/** Quick lookup by id (for the store and tests). */
export const ARTIFACTS_BY_ID = new Map<string, VisualArtifact>(
  VISUAL_ARTIFACTS_CATALOG.map((a) => [a.id, a]),
);
